import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Button,
  Stack,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

/**
 * Modal de confirmación reutilizable (monocromo). La acción de confirmar es destructiva
 * (botón rojo); la de descartar es el CTA blanco seguro por defecto. Acepta etiquetas y
 * una nota secundaria opcional (`note=""` la oculta; p. ej. para cancelaciones donde no
 * aplica el "no se puede deshacer").
 */
function ConfirmDeleteModal({
  open,
  onClose,
  onConfirm,
  title = null,
  message = null,
  confirmLabel = null,
  cancelLabel = null,
  note = null,
  count = 1
}) {
  const { t } = useTranslation(['common', 'buttons']);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: 'surface.content',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'hairline.main'
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
          <WarningIcon sx={{ color: '#ff6b6b', fontSize: 28 }} />
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            {title || t('confirmDelete', { ns: 'common' })}
          </Typography>
        </Stack>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary',
              backgroundColor: 'action.hover'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3, pb: 4 }}>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Mensaje de advertencia (tinte rojo semántico) */}
          <Box
            sx={{
              bgcolor: 'rgba(178, 22, 22, 0.12)',
              p: 3,
              borderRadius: 2,
              border: '1px solid rgba(178, 22, 22, 0.3)',
              textAlign: 'center'
            }}
          >
            <Typography variant="body1" color="text.primary" sx={{ mb: note === '' ? 0 : 1 }}>
              {message || t('confirmDeleteMessage', { count, ns: 'common' })}
            </Typography>
            {note !== '' && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {note || t('actionCannotBeUndone', { ns: 'common' })}
              </Typography>
            )}
          </Box>

          {/* Botones: descartar = CTA blanco seguro · confirmar = rojo destructivo */}
          <Stack direction="row" spacing={2}>
            <Button
              onClick={onClose}
              fullWidth
              sx={{
                py: 1.5,
                borderRadius: 2,
                background: 'var(--otx-cta-bg)',
                color: 'var(--otx-cta-text)',
                fontWeight: 'bold',
                '&:hover': { background: 'var(--otx-cta-bg)', filter: 'brightness(0.94)' }
              }}
            >
              {cancelLabel || t('cancel', { ns: 'buttons' })}
            </Button>
            <Button
              onClick={onConfirm}
              fullWidth
              sx={{
                py: 1.5,
                borderRadius: 2,
                backgroundColor: '#B21616',
                // Botón destructivo SIEMPRE con texto blanco (antes text.primary = negro en
                // claro → ilegible sobre el rojo).
                color: '#FFFFFF',
                fontWeight: 'bold',
                '&:hover': { backgroundColor: '#841010' }
              }}
            >
              {confirmLabel || t('delete', { ns: 'common' })}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

ConfirmDeleteModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  note: PropTypes.string,
  count: PropTypes.number
};

export default ConfirmDeleteModal;
